// src/services/onedriveIntegrationService.js
import axios from 'axios';
import { supabase } from './supabase';

const BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.lawdie.co'
  : 'http://localhost:4000';

class OneDriveIntegrationService {
  constructor() {
    this.axios = axios.create({
      baseURL: BASE_URL,
      withCredentials: true
    });

    // Add auth token to requests if available
    this.axios.interceptors.request.use(async (config) => {
      const authHeader = await this.getAuthHeader();
      if (authHeader) {
        config.headers.Authorization = authHeader;
      }
      return config;
    });
  }

  async getAuthHeader() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      if (!session?.access_token) return null;
      return `Bearer ${session.access_token}`;
    } catch (error) {
      console.error('Error getting auth header:', error);
      return null;
    }
  }

  async getAuthUrl() {
    try {
      const { data } = await this.axios.get('/api/auth/onedrive/url');
      return data.url;
    } catch (error) {
      console.error('Error getting auth URL:', error);
      throw new Error('Failed to get authentication URL for OneDrive');
    }
  }

  async exchangeCode(code, state, userId) {
    try {
      console.log('Exchanging code with userId:', userId);
      
      if (!userId) {
        throw new Error('User ID is required for OneDrive integration');
      }
      
      const { data } = await this.axios.post('/api/auth/onedrive/exchange-code', { 
        code, 
        state,
        userId // Include userId in the request body
      });
      
      return data;
    } catch (error) {
      console.error('Error exchanging code:', error);
      throw new Error(error.response?.data?.error || 'Failed to exchange authorization code');
    }
  }

  async checkIntegrationStatus() {
    try {
      const { data } = await this.axios.get('/api/auth/onedrive/status');
      return {
        connected: data.connected,
        profile: data.profile,
        services: data.services
      };
    } catch (error) {
      console.error('Error checking OneDrive integration status:', error);
      throw new Error('Failed to check OneDrive integration status');
    }
  }

  // In onedriveIntegrationService.js
  async disconnect() {
    try {
      // Get current user ID
      const { data } = await supabase.auth.getSession();
      const userId = data?.session?.user?.id;
  
      if (!userId) {
        throw new Error('No authenticated user found');
      }
  
      // Pass userId in the request body
      const response = await this.axios.post('/api/auth/onedrive/disconnect', { userId });
      return response.data;
    } catch (error) {
      console.error('Error disconnecting OneDrive:', error);
      throw new Error('Failed to disconnect OneDrive integration');
    }
  }

  // OneDrive specific methods
  async listFiles(folderId = null) {
    try {
      const params = {};
      if (folderId) {
        params.folderId = folderId;
      }
      
      const { data } = await this.axios.get('/api/auth/onedrive/list', { params });
      return data;
    } catch (error) {
      console.error('Error listing OneDrive files:', error);
      throw new Error('Failed to list OneDrive files');
    }
  }

  async downloadFile(fileId) {
    try {
      const response = await this.axios.get(`/api/auth/onedrive/download/${fileId}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading OneDrive file:', error);
      throw new Error('Failed to download OneDrive file');
    }
  }

  async uploadFile(file, folderId = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (folderId) {
        formData.append('folderId', folderId);
      }

      const { data } = await this.axios.post('/api/onedrive/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return data;
    } catch (error) {
      console.error('Error uploading file to OneDrive:', error);
      throw new Error('Failed to upload file to OneDrive');
    }
  }

  async createFolder(folderName, parentFolderId = null) {
    try {
      const { data } = await this.axios.post('/api/onedrive/create-folder', {
        name: folderName,
        parentFolderId
      });
      return data;
    } catch (error) {
      console.error('Error creating OneDrive folder:', error);
      throw new Error('Failed to create OneDrive folder');
    }
  }
}

// Create and export a single instance
export const onedriveIntegrationService = new OneDriveIntegrationService();